import { UserRound } from 'lucide-react';
import { useState } from 'react';

import { Avatar as AvatarShadCn, AvatarFallback } from '../core/avatar';

type Props = {
  /* The first and last name of the user */
  name: string | undefined;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  src?: string;
  variant?: 'default' | 'primary';
  /* A user ID. Do not use a name, because two users can have the same name. */
  colorSeed?: string;
  className?: string;
};

const ICON_SIZE_CLASS: Record<NonNullable<Props['size']>, string> = {
  xs: 'size-3',
  sm: 'size-4',
  md: 'size-4',
  lg: 'size-4',
  xl: 'size-5',
};

export const Avatar = ({ name, size = 'sm', src, variant = 'primary', colorSeed, className }: Props) => {
  // Tracked as the URL that failed rather than a boolean so a new `src` retries on its own.
  const [failedSrc, setFailedSrc] = useState<string>();

  // Names starting with "#" are anonymous-participant references (e.g. "#1234567890"), not real names,
  // so show the user icon fallback instead of a "#" initial.
  const isAnonymousHandle = name?.startsWith('#') ?? false;

  // Get first and last initial (or first initial if only one word)
  const initials =
    !name || isAnonymousHandle
      ? ''
      : name
          .split(' ')
          .filter(Boolean)
          .map((word, i, arr) => (i === 0 || i === arr.length - 1 ? word[0] : ''))
          .join('')
          .toUpperCase();

  const showImage = Boolean(src) && src !== failedSrc;

  return (
    <AvatarShadCn size={size} data-testid="avatar" className={className}>
      {/*
        Kept mounted under the image rather than swapped out, so there's no gap while it loads —
        but hidden from assistive tech whenever an image is rendered, or the initials would be
        announced alongside the image's alt text. Both read out the same person.
      */}
      <AvatarFallback variant={variant} colorSeed={colorSeed} aria-hidden={showImage || undefined}>
        {initials || <UserRound className={ICON_SIZE_CLASS[size]} role="img" />}
      </AvatarFallback>
      {showImage ? (
        // A native <img> is what lets the browser hold the request back until the avatar is near
        // the viewport, and widen that distance on a slow connection. Radix's AvatarImage fetches
        // through a detached `new Image()` in a layout effect, so the request is in flight before
        // any element exists and the browser has no position to decide from — a list of several
        // hundred avatars then fires several hundred requests in one pass before first paint.
        <img
          src={src}
          alt={name ?? ''}
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          onError={() => setFailedSrc(src)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : null}
    </AvatarShadCn>
  );
};
